import { useEffect, useState } from "react";
import SuccessModal from "../SuccessModal";
import ProgressBar from "../../pages/ProgressBar";
import BankDetailsStep from "../../pages/BankDetailsStep";
import BasicDetailsStep from "../../pages/BasicDetailsStep";
import BusinessDetailsStep from "../../pages/BusinessDetailsStep";
import KYCVerificationStep from "../../pages/KYCVerificationStep";
import { config } from "../../utils/config";
import axios from "axios";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
const TOTAL_STEPS = 4;

// Key used to persist which onboarding step the seller is on, so a
// refresh/resume lands them back where they left off instead of step 1.
const STEP_STORAGE_KEY = "onboarding_step";

const TOAST_STYLE = {
    style: {
        background: "#fde9e9",
        borderRadius: "10px",
        padding: "14px",
        fontSize: "15px",
        border: "1px solid red"
    },
    hideProgressBar: true,
};

export default function OnboardingForm() {
    // FIX: lazy-init currentStep from sessionStorage so onboarding resumes
    // on the correct step instead of always restarting at 1.
    const [currentStep, setCurrentStep] = useState(() => {
        if (typeof window === "undefined") return 1;
        const saved = sessionStorage.getItem(STEP_STORAGE_KEY);
        const parsed = Number(saved);
        return parsed >= 1 && parsed <= TOTAL_STEPS ? parsed : 1;
    });
    const [bankSuccess, setBankSuccess] = useState(false);
    const [bankData, setBankData] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [prefillBasicData, setPrefillBasicData] = useState({});
    const [prefillBankData, setPrefillBankData] = useState({});
    const { apiEndpoint } = config;
    const [isPanVerified, setIsPANVerified] = useState(false);
    const [isGSTVerified, setIsGSTVerified] = useState(false);
    const [showPanField, setShowPanfield] = useState(false);
    const [isBankValid, setIsBankValid] = useState(false);
    const [isBankVerified, setIsBankVerified] = useState(false);
    const [taxType, setTaxType] = useState("gst");
    const [AadharVerified, setIsAadharVerified] = useState(false);
    const [formData, setFormData] = useState({
        business: {},
        basic: {},
        bank: {},
        kyc: {}
    });

    // FIX: keep sessionStorage in sync with currentStep so Next/Back/auto-advance
    // (bank verify, business submit, etc.) all persist the resume point.
    useEffect(() => {
        sessionStorage.setItem(STEP_STORAGE_KEY, String(currentStep));
    }, [currentStep]);

    const verifyGST = async (gst_number) => {
        try {
            const token = sessionStorage.getItem("us_st_d");
            const response = await fetch(`${apiEndpoint}/verify-gst`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ gst_number }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || "GST verification failed");
            }
            return data;
        } catch (error) {
            console.log("GST Verify Error:", error.message);
            throw error;
        }
    };

    const verifyPanDetails = async (pan_name) => {
        try {
            const token = sessionStorage.getItem("us_st_d");
            // FIX: there were two "headers" keys in this object before — the second
            // silently overwrote the first. Kept a single, correct headers object.
            const res = await fetch(`${apiEndpoint}/generate-digilocker-url`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    aadhar_no: pan_name,
                }),
            });

            return await res.json();
        } catch (err) {
            toast.error("Something went wrong while generating the DigiLocker link.", TOAST_STYLE);
            return null;
        }
    };

    // NOTE: this API key is exposed to every client since it's bundled into
    // frontend JS. Ideally this call should be proxied through your backend
    // so the key never ships to the browser.
    const apiKey = `sV2DUZ52gtPE82AAmbhljWm19H2jJVBnCQvXglZo`;
    const getLatLng = async (pincode) => {
        try {
            const res = await fetch(
                `https://api.olamaps.io/places/v1/geocode?address=${pincode}&api_key=${apiKey}`
            );
            const data = await res.json();
            const location = data?.geocodingResults?.[0]?.geometry?.location;
            return {
                lat: location?.lat || "",
                lng: location?.lng || ""
            };
        } catch (err) {
            console.log("Geocode Error:", err);
            return { lat: "", lng: "" };
        }
    };

    const handleNext = async () => {
        try {
            if (currentStep === 2) {
                if (!AadharVerified) {
                    toast.error("Please verify", TOAST_STYLE);
                    return;
                }
                setCurrentStep((prev) => prev + 1);
                setLoading(true);
                // FIX: missing return caused this branch to fall through into
                // the generic fetch(url, ...) below with an empty url ("")
                // firing a broken network call on every step 2 -> 3 move.
                return;
            }
            if (currentStep === TOTAL_STEPS) {
                await activeSellerHandler();
                setShowSuccess(true);
                return;
            }
            setLoading(true);
            let payload = {};
            let url = "";
            const token = sessionStorage.getItem("us_st_d");
            if (currentStep === 1) {
                const b = formData.business;

                // FIX: was comparing to "" only, so undefined values slipped through
                if (!b.street || !b.locality || !b.city || !b.state || !b.pincode) {
                    toast.error("Please enter all fields", TOAST_STYLE);
                    setLoading(false);
                    return;
                }

                const { lat, lng } = await getLatLng(b.pincode);
                const latitude = lat?.toString();
                const longitude = lng?.toString();

                payload = {
                    bussiness_type: b.bussiness_type,
                    company_name: b.company_name,
                    street: b.street,
                    city: b.city,
                    area_code: b.pincode,
                    state: b.state,
                    country: b.country || "India",
                    latitude: latitude,
                    longitude: longitude,
                    locality: b.locality,
                    pan_name: b.pan_name,
                    gst_no: b.gst_number,
                    pan_no: b.pan_number,
                    verification_type: taxType === "nonGst" ? "pan" : "gst"
                };
                url = `${apiEndpoint}/insert-business-details`;
            }

            if (currentStep === 3) {
                const { accountnumber, ifsc, name } = formData.bank;
                // FIX: was comparing to "" only, so undefined values slipped through
                if (!accountnumber || !ifsc || !name) {
                    toast.error("Please fill all details.", TOAST_STYLE);
                    setLoading(false);
                    return;
                }
                if (!formData?.bank?.isVerifiedBank) {
                    payload = formData.bank;
                    url = `${apiEndpoint}/verify-bank`;
                } else {
                    setBankSuccess(true);
                    setTimeout(() => {
                        setCurrentStep((prev) => prev + 1);
                    }, 1000);
                    return;
                }
            }

            const res = await fetch(url, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (!res.ok) {
                console.log("API Error", data);
                // FIX: previously failed silently with only a console.log —
                // user had no idea why the button stopped loading.
                toast.error(data?.message || "Something went wrong. Please try again.", TOAST_STYLE);
                setLoading(false);
                return;
            }

            if (currentStep === 3) {
                sessionStorage.setItem("bankVerified", "true");
                setTimeout(() => {
                    setCurrentStep((prev) => prev + 1);
                }, 1000);
                return;
            }

            if (currentStep < TOTAL_STEPS) {
                setCurrentStep((prev) => prev + 1);
            } else {
                setShowSuccess(true);
            }
        } catch (err) {
            console.log(err);
            toast.error("Something went wrong. Please try again.", TOAST_STYLE);
        } finally {
            setLoading(false);
        }
    };

    const getAllbusinessInfo = async () => {
        try {
            const token = sessionStorage.getItem("us_st_d");
            const res = await axios({
                url: `${apiEndpoint}/step-one`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`
                },
                data: {
                    gst_no: formData?.business?.gst_number
                }
            });
            const apiData = res?.data?.data;
            setTaxType(apiData?.verification_type == "pan" ? "nonGst" : "gst");
            if (apiData?.verification_type == "pan" || apiData?.verification_type == "gst") {
                setIsPANVerified(true);
            }

            if (apiData?.gst_no) {
                setShowPanfield(true);
                setIsGSTVerified(true);
            } else {
                setShowPanfield(false);
                setIsGSTVerified(false);
            }
            return res.data;
        } catch (error) {
            console.log("Error ------>", error);
            return null;
        }
    };

    const getBankDetails = async () => {
        try {
            const token = sessionStorage.getItem("us_st_d");
            const res = await axios({
                url: `${apiEndpoint}/get-bank-details`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`
                }
            });
            return res.data;
        } catch (error) {
            console.log("Bank Details Error:", error);
            return null;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            const data = await getAllbusinessInfo();
            if (data?.data) {
                const apiData = data.data;
                setFormData((prev) => ({
                    ...prev,
                    business: {
                        ...prev.business,
                        gst_number: apiData?.gst_no || "",
                        company_name: apiData?.company_name || "",
                        bussiness_type: apiData?.bussiness_type,
                        pan_number: apiData?.pan_no || "",
                        pan_name: apiData?.pan_name,
                        street: apiData?.street || "",
                        city: apiData?.city || "",
                        pincode: apiData?.area_code || "",
                        state: apiData?.state || "",
                        locality: apiData?.locality || "",
                        country: "India"
                    }
                }));
                setPrefillBasicData(apiData);
                setIsGSTVerified(true);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (currentStep === 3) {
            const fetchBankData = async () => {
                const data = await getBankDetails();
                if (data) {
                    const apiData = data;
                    setFormData((prev) => ({
                        ...prev,
                        bank: {
                            ...prev.bank,
                            accountnumber: apiData?.accountnumber || "",
                            ifsc: apiData?.ifsc || "",
                            bankname: apiData?.bank_name || "",
                            name: apiData?.name || "",
                            branchname: apiData?.branch_name || "",
                            isVerifiedBank: apiData?.accountnumber ? true : false
                        }
                    }));
                    setPrefillBankData(apiData);
                    if (data?.is_verified) {
                        setBankSuccess(true);
                        setBankData(true);
                        sessionStorage.setItem("bankVerified", "true");
                    }
                }
            };
            fetchBankData();
        }
    }, [currentStep]);

    const handleBack = async () => {
        const prevStep = currentStep - 1;
        if (currentStep > 1) {
            if (prevStep === 1) {
                const data = await getAllbusinessInfo();
                if (data?.data) {
                    const apiData = data.data;
                    setFormData((prev) => ({
                        ...prev,
                        business: {
                            ...prev.business,
                            gst_number: apiData?.gst_no || "",
                            company_name: apiData?.company_name || "",
                            bussiness_type: apiData?.bussiness_type,
                            pan_number: apiData?.pan_no || "",
                            street: apiData?.street || "",
                            city: apiData?.city || "",
                            pincode: apiData?.area_code || "",
                            state: apiData?.state || "",
                            country: "India"
                        },
                    }));
                    setPrefillBasicData(apiData);
                    setIsGSTVerified(true);
                }
            }
            if (prevStep === 3) {
                const bankRes = await getBankDetails();
                if (bankRes) {
                    setFormData((prev) => ({
                        ...prev,
                        bank: {
                            accountnumber: bankRes?.bankaccount || "",
                            ifsc: bankRes?.ifsc || "",
                            beneficiaryname: bankRes?.beneficiaryname || "",
                            bankname: bankRes?.bankname || "",
                            branchname: bankRes?.branchname || ""
                        }
                    }));
                }
            }
            setCurrentStep(prevStep);
        }
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <BusinessDetailsStep
                        taxType={taxType}
                        setTaxType={setTaxType}
                        data={formData.business}
                        setData={(data) =>
                            setFormData((prev) => ({ ...prev, business: data }))
                        }
                        verifyGST={verifyGST}
                        verifyPanDetails={verifyPanDetails}
                        isGSTVerified={isGSTVerified}
                        setIsGSTVerified={setIsGSTVerified}
                        isPANVerified={isPanVerified}
                        setIsPANVerified={setIsPANVerified}
                        setShowPanfield={setShowPanfield}
                        showPanField={showPanField}
                    />
                );

            case 2:
                return (
                    <BasicDetailsStep
                        data={formData.basic}
                        setData={(value) =>
                            setFormData((prev) => ({
                                ...prev,
                                basic: typeof value === "function"
                                    ? value(prev.basic)
                                    : value
                            }))
                        }
                        verifyPanDetails={verifyPanDetails}
                        isPANVerified={isPanVerified}
                        setIsPANVerified={setIsPANVerified}
                        setPrefillBasicData={setPrefillBasicData}
                        prefillBasicData={prefillBasicData}
                        getAllbusinessInfo={getAllbusinessInfo}
                        AadharVerified={AadharVerified}
                        setIsAadharVerified={setIsAadharVerified}
                    />
                );

            case 3:
                return (
                    <BankDetailsStep
                        data={formData.bank}
                        setData={(data) =>
                            setFormData((prev) => ({ ...prev, bank: data }))
                        }
                        getBankDetails={getBankDetails}
                        setPrefillBankData={setPrefillBankData}
                        bankSuccess={bankSuccess}
                        setBankData={setBankData}
                        prefillBankData={prefillBankData}
                        loading={loading}
                        setIsBankValid={setIsBankValid}
                        isBankValid={isBankValid}
                        isBankVerified={isBankVerified}
                    />
                );

            case 4:
                return (
                    <KYCVerificationStep
                        data={formData.kyc}
                        setData={(data) =>
                            setFormData((prev) => ({ ...prev, kyc: data }))
                        }
                    />
                );
            default:
                return null;
        }
    };

    const isKYCValid =
        formData?.kyc?.terms &&
        formData?.kyc?.commission &&
        formData?.kyc?.refund;

    const activeSellerHandler = async () => {
        setLoading(true);
        try {
            const token = sessionStorage.getItem("us_st_d");
            const res = await axios({
                url: `${apiEndpoint}/activate-seller`,
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Custom-Authorization": `Bearer ${token}`
                },
            });
            if (res?.data?.token) {
                sessionStorage.setItem("us_st_d", res?.data?.token);
            }
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.log("Error ------>", error);
            toast.error("Couldn't activate your seller account. Please try again.", TOAST_STYLE);
            return null;
        }
    };

    return (
        <>
            <SuccessModal show={showSuccess} onClose={() => setShowSuccess(false)} />
            <div className="bg-light min-vh-100">
                <div className="container px-5">
                    <ProgressBar currentStep={currentStep} />
                </div>

                <main className="container pb-3">
                    <div className="px-5">

                        {renderStep()}

                        <div className="d-flex justify-content-between mt-4">

                            {currentStep > 1 ? (
                                <button
                                    onClick={handleBack}
                                    className="px-4 rounded fw-semibold"
                                    style={{ border: "1px solid #F75710 !important", color: "#F75710" }}
                                >
                                    Back
                                </button>
                            ) : <div />}

                            <button
                                onClick={() => {
                                    if (window.gtag) {
                                        window.gtag("event", "next_button_clicked", {
                                            event_category: "KYC",
                                            event_label: `Step_${currentStep}_Next_Clicked`,
                                        });
                                    }
                                    handleNext()
                                }}
                                disabled={
                                    loading ||
                                    (currentStep === TOTAL_STEPS && !isKYCValid)
                                }
                                className="text-white px-4 py-2 rounded fw-semibold"
                                style={{
                                    background: "linear-gradient(90deg, #F75710, #FC8809)",
                                    border: "none",
                                    opacity: (currentStep === TOTAL_STEPS && !isKYCValid) ? 0.6 : 1
                                }}
                            >
                                {loading
                                    ? "Processing..."
                                    : currentStep === TOTAL_STEPS
                                        ? "Submit"
                                        : "Next"}
                            </button>
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
}